import { CheckOutlined, CopyOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Input, Spin, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getSupportDoc } from '../../services/supportApi';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import CellCogMarkdown from '../Common/CellCogMarkdown';
import QuickActionChip from '../Common/QuickActionChip';
import RichCodeBlock from '../Common/RichCodeBlock';
import SUPPORT_DOCS, { fileToSlug, getCategories, slugToFile } from './supportDocConfig';
import './SupportDocsPage.css';

const SupportDocsPage = () => {
  const { docSlug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [selectedFile, setSelectedFile] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const categories = useMemo(() => getCategories(), []);

  // Determine active category from selected doc
  const currentDoc = SUPPORT_DOCS.find(d => d.file === selectedFile);
  const activeCategory = currentDoc ? currentDoc.category : categories[0];

  // Resolve initial doc from URL slug or default to first doc
  useEffect(() => {
    if (docSlug) {
      const file = slugToFile(docSlug);
      if (file) {
        setSelectedFile(file);
      } else {
        navigate(`/support/${fileToSlug(SUPPORT_DOCS[0].file)}`, { replace: true });
      }
    } else {
      navigate(`/support/${fileToSlug(SUPPORT_DOCS[0].file)}`, { replace: true });
    }
  }, [docSlug, navigate]);

  // Fetch markdown content when selected file changes
  const fetchContent = useCallback(async (filename) => {
    if (!filename) return;
    setLoading(true);
    try {
      const response = await getSupportDoc(filename);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to fetch support doc:', error);
      setContent('# Error\n\nFailed to load this document. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedFile) {
      fetchContent(selectedFile);
    }
  }, [selectedFile, fetchContent]);

  const handleSelectDoc = (filename) => {
    const slug = fileToSlug(filename);
    navigate(`/support/${slug}`);
    setSearchValue('');
  };

  const handleCategoryClick = (category) => {
    const firstDoc = SUPPORT_DOCS.find(d => d.category === category);
    if (firstDoc) {
      handleSelectDoc(firstDoc.file);
    }
  };

  const handleCopyMarkdown = async () => {
    if (!content || copied) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail
    }
  };

  // Custom link renderer: intercept internal ./foo.md links and route
  // them via react-router instead of opening them in a new tab. External
  // links get the default open-in-new-tab behavior.
  const linkRenderer = useCallback(({ href, children, ...props }) => {
    if (href && href.startsWith('./') && href.endsWith('.md')) {
      const filename = href.replace('./', '');
      const matchingDoc = SUPPORT_DOCS.find(d => d.file === filename);
      if (matchingDoc) {
        return (
          <a
            href={`/support/${fileToSlug(filename)}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/support/${fileToSlug(filename)}`);
            }}
            {...props}
          >
            {children}
          </a>
        );
      }
    }
    return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
  }, [navigate]);

  const markdownExtras = useMemo(() => ({ a: linkRenderer }), [linkRenderer]);

  // Docs in active category
  const activeCategoryDocs = SUPPORT_DOCS.filter(d => d.category === activeCategory);

  // Search results (title/category filtering)
  const searchResults = searchValue
    ? SUPPORT_DOCS.filter(doc =>
        doc.title.toLowerCase().includes(searchValue.toLowerCase()) ||
        doc.category.toLowerCase().includes(searchValue.toLowerCase())
      )
    : [];

  const seoTitle = currentDoc
    ? `${currentDoc.title} - CellCog Support`
    : 'Support Docs - CellCog';
  // Each guide opens with a one-paragraph summary under its H1; use it as the description.
  const docSummary = useMemo(() => {
    const para = (content || '').split('\n').map(l => l.trim())
      .find(l => l && !l.startsWith('#') && !l.startsWith('---') && !l.startsWith('>'));
    if (!para) return '';
    const plain = para.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/[*_`]/g, '');
    return plain.length > 155 ? `${plain.slice(0, 152).replace(/\s+\S*$/, '')}...` : plain;
  }, [content]);
  const seoDescription = currentDoc
    ? (docSummary || `CellCog support documentation: ${currentDoc.title}. Learn how to use CellCog's features and capabilities.`)
    : 'Browse CellCog support documentation. Guides for chat, projects, billing, integrations, and more.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/support${currentDoc ? '/' + fileToSlug(currentDoc.file) : ''}`} />
        {/* Agent-readable alternate (CEL-2097): emitted by scripts/copySupportDocs.js at prebuild. */}
        {currentDoc && (
          <link rel="alternate" type="text/markdown" href={`${SEO_DEFAULTS.url}/support/${fileToSlug(currentDoc.file)}.md`} />
        )}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/support${currentDoc ? '/' + fileToSlug(currentDoc.file) : ''}`} />
        <meta property="og:image" content={SEO_DEFAULTS.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>

      <div className={`support-docs-page ${isDarkMode ? 'dark' : 'light'}`}>
        {/* Top navigation header */}
        <div className="support-docs-header">
          <div className="support-docs-header-inner">
            {/* Title row with Quick Action and Search */}
            <div className="support-docs-title-row">
              <div className="support-docs-title">Support</div>
              <div className="support-docs-title-actions">
                <QuickActionChip
                  label="Search via Agent"
                  action="support_search"
                  size="medium"
                />
                <Input
                  placeholder="Search docs..."
                  prefix={<SearchOutlined />}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  allowClear
                  className="support-docs-search"
                />
              </div>
            </div>

            {/* Search results dropdown */}
            {searchValue && (
              <div className="support-docs-search-results">
                {searchResults.length > 0 ? (
                  searchResults.map(doc => (
                    <div
                      key={doc.file}
                      className={`support-docs-search-item ${selectedFile === doc.file ? 'active' : ''}`}
                      onClick={() => handleSelectDoc(doc.file)}
                    >
                      <span className="support-docs-search-item-category">{doc.category}</span>
                      <span className="support-docs-search-item-title">{doc.title}</span>
                    </div>
                  ))
                ) : (
                  <div className="support-docs-search-empty">No results found</div>
                )}
              </div>
            )}

            {/* Category tabs and doc picker (hidden during search) */}
            {!searchValue && (
              <>
                <div className="support-docs-categories">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      className={`support-docs-category-tab ${activeCategory === cat ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {activeCategoryDocs.length > 1 && (
                  <div className="support-docs-doc-picker">
                    {activeCategoryDocs.map(doc => (
                      <button
                        key={doc.file}
                        className={`support-docs-doc-tab ${selectedFile === doc.file ? 'active' : ''}`}
                        onClick={() => handleSelectDoc(doc.file)}
                      >
                        {doc.title}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Content area */}
        <div className="support-docs-content">
          <div className="support-docs-content-scroll">
            {/* Copy button */}
            {content && !loading && (
              <div className="support-docs-copy-row">
                <Tooltip title={copied ? '' : 'Copy raw markdown'}>
                  <Button
                    type="text"
                    icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyMarkdown}
                    size="small"
                    className={`support-docs-copy-btn ${copied ? 'copied' : ''}`}
                  >
                    {copied ? 'Copied' : 'Copy Markdown'}
                  </Button>
                </Tooltip>
              </div>
            )}

            {loading ? (
              <div className="support-docs-empty">
                <Spin size="large" />
              </div>
            ) : content ? (
              <div className="support-docs-content-inner">
                <CellCogMarkdown
                  content={content}
                  isDarkMode={isDarkMode}
                  enableCellCogTags="none"
                  codeBlockComponent={RichCodeBlock}
                  richCodeDefaultMode="raw"
                  extraComponents={markdownExtras}
                />
              </div>
            ) : (
              <div className="support-docs-empty">
                <Empty description="Select a document to view" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportDocsPage;
